/**
 * System: Kampf — Fähigkeiten wirken, Schaden entsteht.
 *
 * Zuständigkeit: Auswahl und Wirkung von Spielerfähigkeiten sowie die zentrale
 * Schadensberechnung in beide Richtungen. Wer Schaden zufügt, geht durch die
 * beiden Funktionen `trefferAufGegner` und `schadenAmSpieler` — nirgends sonst.
 */

import type { EffektSicht, Ereignis } from '@pg/protocol';
import { TAKT_DAUER } from '@pg/protocol';
import type { Zufall } from '@pg/rng';
import type { GegnerWesen, Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { baueLagebild, waehleFaehigkeit } from '../regeln.js';
import { staerkungMinderung, staerkungSchaden } from './spieler.js';
import { abstand, imKegel, laenge, richtungVon, vek, winkelVon } from '../vektor.js';
import { tempo } from '../tempo.js';

export function faehigkeiten(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  if (s.tot || s.wirktNoch > 0) return;

  const r = ctx.inhalte.regelwerk;
  const lage = baueLagebild(s, welt.gegner, r);
  const wahl = waehleFaehigkeit(s, lage, welt.karte, r);

  // Der Kampfstrom zieht in JEDEM Takt genau einmal — auch wenn nichts
  // gewirkt wird. Sonst verschiebt eine Positionsänderung den ganzen Strom
  // und kein goldener Lauf ist mehr vergleichbar.
  const kampf = ctx.zufall.strom('kampf');
  kampf.zahl();

  if (!wahl) return;

  const slot = s.slots[wahl.slot];
  if (!slot) return;
  const f = slot.faehigkeit;

  const gangart = tempo(welt, ctx.inhalte);
  slot.manuellAngefordert = false;
  slot.manuellZiel = null;
  slot.abklingen = f.abklingen * gangart.abklingen;
  s.vorrat = Math.max(0, s.vorrat - f.vorrat);
  // Der Grundangriff haelt NIE an — sonst nimmt die Automatik dem Spieler
  // die Bewegung weg (siehe Regelwerk, faehigkeiten._automatikgrad).
  s.wirktNoch = f.grundangriff ? 0 : f.wirkdauer * gangart.wirkdauer;
  s.wirktGesamt = s.wirktNoch;

  // Ausrichtung: manuelles Ziel schlägt Automatik, sonst der nächste Gegner.
  // Bei Bewegungsfähigkeiten gilt IMMER die Spielerrichtung (Ueberarbeitung B9a).
  let richtung = s.richtung;
  const versetzt = (f.eigenbewegung ?? 0) > 0;
  if (versetzt && laenge(s.richtung) > 0.08) {
    richtung = s.richtung;
  } else if (wahl.ziel) {
    richtung = richtungVon(s.pos, wahl.ziel);
  } else if (lage.naechster) {
    richtung = richtungVon(s.pos, lage.naechster.pos);
  }
  if (laenge(richtung) > 0.01) s.winkel = winkelVon(richtung);

  const krit = ctx.zufall.strom('kritisch');
  const schaden = ((f.schaden ?? 0) + s.schaden) * staerkungSchaden(s) * gangart.schaden;

  switch (f.form) {
    case 'bogen': {
      const reichweite = f.reichweite ?? 1.5;
      const halberWinkel = (f.winkel ?? 1.9) / 2;
      for (const g of welt.gegner) {
        if (g.tot) continue;
        if (abstand(s.pos, g.pos) > reichweite + g.radius) continue;
        if (!imKegel(s.pos, richtung, g.pos, halberWinkel)) continue;
        trefferAufGegner(welt, ctx, g, schaden, krit, f.brennt ?? null);
      }
      ctx.effekte.push({ art: 'schlag', pos: { ...s.pos }, wert: reichweite, takt: welt.takt });
      break;
    }

    case 'ring': {
      const reichweite = f.reichweite ?? 2.5;
      for (const g of welt.gegner) {
        if (g.tot) continue;
        if (abstand(s.pos, g.pos) > reichweite + g.radius) continue;
        trefferAufGegner(welt, ctx, g, schaden, krit, f.brennt ?? null);
      }
      ctx.effekte.push({ art: 'schlag', pos: { ...s.pos }, wert: reichweite, takt: welt.takt });
      break;
    }

    case 'schub': {
      const reichweite = f.reichweite ?? 3;
      s.stoss.x += richtung.x * (f.eigenbewegung ?? 3) * 8;
      s.stoss.y += richtung.y * (f.eigenbewegung ?? 3) * 8;
      for (const g of welt.gegner) {
        if (g.tot) continue;
        if (abstand(s.pos, g.pos) > reichweite + g.radius) continue;
        if (!imKegel(s.pos, richtung, g.pos, 1.1)) continue;
        trefferAufGegner(welt, ctx, g, schaden, krit, null);
        const weg = richtungVon(s.pos, g.pos);
        g.stoss.x += weg.x * (f.wegdruecken ?? 2) * 8;
        g.stoss.y += weg.y * (f.wegdruecken ?? 2) * 8;
      }
      ctx.effekte.push({ art: 'schlag', pos: { ...s.pos }, wert: reichweite, takt: welt.takt });
      break;
    }

    case 'geschoss': {
      const def = f.geschoss ? ctx.inhalte.geschosse.get(f.geschoss) : undefined;
      if (def) {
        welt.geschosse.push({
          id: welt.naechsteId++,
          bauplan: def.id,
          pos: vek(s.pos.x + richtung.x * 0.5, s.pos.y + richtung.y * 0.5),
          gesch: vek(richtung.x * def.tempo, richtung.y * def.tempo),
          radius: def.radius,
          schaden,
          verbleibend: def.lebensdauer,
          vomSpieler: true,
          tot: false,
        });
      }
      break;
    }

    case 'selbst': {
      if (f.staerkung) {
        s.staerkungen.push({
          quelle: f.id,
          schaden: f.staerkung.schaden ?? 0,
          schadensminderung: f.staerkung.schadensminderung ?? 0,
          verbleibend: f.staerkung.dauer,
        });
      }
      ctx.effekte.push({ art: 'schlag', pos: { ...s.pos }, wert: 1.2, takt: welt.takt });
      break;
    }
  }
}

export function trefferAufGegner(
  welt: Welt,
  ctx: Ctx,
  g: GegnerWesen,
  grundschaden: number,
  kritStrom: ReturnType<Zufall['strom']>,
  brennt: { schaden: number; dauer: number } | null,
): void {
  const s = welt.spieler;
  const istKrit = kritStrom.chance(s.kritchance);
  let schaden = grundschaden * (istKrit ? 1 + s.kritschaden : 1);

  // Merkmal 'abweisend': mindert eingehenden Schaden.
  for (const m of g.merkmale) {
    if (m.art === 'minderung' && m.anteil) schaden *= 1 - m.anteil;
  }
  const gerundet = Math.max(1, Math.round(schaden));

  g.leben -= gerundet;
  ctx.effekte.push({
    art: istKrit ? 'kritisch' : 'treffer',
    pos: { ...g.pos },
    wert: gerundet,
    takt: welt.takt,
  });

  if (brennt) g.brennt = { schaden: brennt.schaden, verbleibend: brennt.dauer };

  if (s.lebensentzug > 0) {
    s.leben = Math.min(s.lebenMax, s.leben + Math.max(1, Math.round(gerundet * s.lebensentzug)));
  }

  if (g.leben <= 0 && !g.tot) {
    g.tot = true;
    g.totSeit = welt.takt;
  }
}

export function schadenAmSpieler(
  welt: Welt,
  ctx: Ctx,
  roh: number,
  art: 'wucht' | 'glut',
): void {
  const s = welt.spieler;
  if (s.tot) return;
  const r = ctx.inhalte.regelwerk.kampf;

  let schaden = roh;
  if (art === 'wucht') {
    const haerte = r.ruestungHaerte.basis + welt.tiefe * r.ruestungHaerte.jeTiefe;
    schaden *= 1 - Math.min(r.ruestungHaerte.deckel, s.ruestung / (s.ruestung + haerte));
  } else {
    schaden *= 1 - s.widerstandGlut;
  }
  schaden *= 1 - staerkungMinderung(s);
  schaden = Math.max(1, Math.round(schaden));

  s.leben -= schaden;
  s.ohneSchadenSeit = 0;
  ctx.effekte.push({ art: 'treffer', pos: { ...s.pos }, wert: schaden, takt: welt.takt });
  ctx.ereignisse.push({ art: 'spieler-getroffen', schaden, verbleibend: Math.max(0, s.leben) });

  if (s.leben <= 0) {
    s.leben = 0;
    s.tot = true;
    s.totSeit = welt.takt;
    welt.stand = 'gescheitert';
    ctx.ereignisse.push({ art: 'spieler-gestorben' });
  }
}

/** Zustandsschaden über Zeit — tickt sekundenweise, nicht je Takt. */
export function zustandseffekte(welt: Welt, ctx: Ctx): void {
  const takteJeSekunde = Math.round(ctx.inhalte.regelwerk.kampf.brandTaktSekunden / TAKT_DAUER);
  if (welt.takt % takteJeSekunde !== 0) return;

  for (const g of welt.gegner) {
    if (g.tot || !g.brennt) continue;
    g.leben -= g.brennt.schaden;
    g.brennt.verbleibend -= ctx.inhalte.regelwerk.kampf.brandTaktSekunden;
    ctx.effekte.push({ art: 'treffer', pos: { ...g.pos }, wert: g.brennt.schaden, takt: welt.takt });
    if (g.brennt.verbleibend <= 0) g.brennt = null;
    if (g.leben <= 0 && !g.tot) {
      g.tot = true;
      g.totSeit = welt.takt;
    }
  }
}

export type { Ereignis, EffektSicht };
